/*
 * Pure state for configuration forms (VIS-321). A form's values are a flat
 * record keyed by field id, or `id:language` for a translatable field, so each
 * content language is tracked, counted and validated on its own. Values are
 * kept exactly as entered: number fields stay strings until the form's own
 * save maps them.
 */

export type FieldValue = string | boolean
export type FormValues = Record<string, FieldValue>

export type FieldKind = 'text' | 'textarea' | 'number' | 'checkbox' | 'select'

export type FieldDefinition = {
  id: string
  section: string
  kind: FieldKind
  label: string
  help?: string
  required?: boolean
  /** One value per content language. */
  translatable?: boolean
  inputMode?: 'numeric' | 'decimal'
  maxLength?: number
  options?: { value: string; label: string }[]
  /** Conditional field: rendered only while this returns true. */
  visible?: (values: FormValues) => boolean
}

export type SectionDefinition = {
  id: string
  title: string
  /** Shown on the collapsed header, from the saved values. */
  summary?: (baseline: FormValues) => string
}

export type ContentLanguage = { value: string; label: string }

/** Field key → message catalogue key. */
export type ValidationResult = Record<string, string>

export type FormDefinition = {
  sections: SectionDefinition[]
  fields: FieldDefinition[]
  /** The first is the default. Omit, or give one, for a form with no translatable content. */
  contentLanguages?: ContentLanguage[]
  validate: (values: FormValues) => ValidationResult
}

export function fieldKey(field: Pick<FieldDefinition, 'id' | 'translatable'>, language: string) {
  return field.translatable ? `${field.id}:${language}` : field.id
}

export function parseKey(key: string): { id: string; language?: string } {
  const [id, language] = key.split(':')
  return language ? { id, language } : { id }
}

export function fieldForKey(definition: FormDefinition, key: string) {
  const { id } = parseKey(key)
  return definition.fields.find((field) => field.id === id)
}

/** Keys whose value differs from the saved value; putting a field back removes it. */
export function dirtyKeys(values: FormValues, baseline: FormValues): string[] {
  const keys = new Set([...Object.keys(baseline), ...Object.keys(values)])
  return [...keys].filter((key) => values[key] !== baseline[key])
}

export function countBySection(definition: FormDefinition, keys: string[]): Record<string, number> {
  const counts: Record<string, number> = {}
  for (const key of keys) {
    const field = fieldForKey(definition, key)
    if (!field) continue
    counts[field.section] = (counts[field.section] ?? 0) + 1
  }
  return counts
}

export function countByLanguage(keys: string[]): Record<string, number> {
  const counts: Record<string, number> = {}
  for (const key of keys) {
    const { language } = parseKey(key)
    if (language) counts[language] = (counts[language] ?? 0) + 1
  }
  return counts
}

/** Error keys in form order: section by section, field by field, then language. */
export function orderErrorKeys(definition: FormDefinition, errors: Record<string, unknown>): string[] {
  const languages = definition.contentLanguages?.map((language) => language.value) ?? []
  const order = new Map<string, number>()
  let index = 0
  for (const section of definition.sections) {
    for (const field of definition.fields.filter((entry) => entry.section === section.id)) {
      if (field.translatable) for (const language of languages) order.set(`${field.id}:${language}`, index++)
      else order.set(field.id, index++)
    }
  }
  return Object.keys(errors).sort((a, b) => (order.get(a) ?? index) - (order.get(b) ?? index))
}

/** The values a definition starts from, with every translatable field present in every language. */
export function initialFormValues(
  definition: FormDefinition,
  source: (field: FieldDefinition, language?: string) => FieldValue | undefined,
): FormValues {
  const languages = definition.contentLanguages?.map((language) => language.value) ?? []
  const values: FormValues = {}
  for (const field of definition.fields) {
    const empty = field.kind === 'checkbox' ? false : ''
    if (field.translatable) {
      for (const language of languages) values[`${field.id}:${language}`] = source(field, language) ?? empty
    } else {
      values[field.id] = source(field) ?? empty
    }
  }
  return values
}

/**
 * Rejection from a form's save that the form maps onto its fields. Field
 * errors are keyed like values; messages are shown as given. Anything that
 * cannot be mapped is listed in the error summary as text.
 */
export class FormSaveError extends Error {
  fieldErrors: Record<string, string>
  messages: string[]

  constructor({ fieldErrors = {}, messages = [] }: { fieldErrors?: Record<string, string>; messages?: string[] }) {
    super('The form could not be saved')
    this.name = 'FormSaveError'
    this.fieldErrors = fieldErrors
    this.messages = messages
  }
}
